import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FolderOpen, FileText, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { TemplateHub, CreateHubRequest, UpdateHubRequest, TemplateHubTree } from '@/lib/api/types/hubs'
import { HUB_COLORS } from './HubCard'

// Default color value
const DEFAULT_COLOR = 'from-blue-400 to-blue-600'

// Available hub icons for selection
const HUB_ICON_OPTIONS = [
  { name: 'folder', label: 'Folder', icon: FolderOpen },
  { name: 'file', label: 'File', icon: FileText },
]

interface CreateHubDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateHubRequest) => Promise<void>
  parentHubs?: TemplateHubTree[]
  initialParentId?: string
}

export function CreateHubDialog({
  open,
  onOpenChange,
  onSubmit,
  parentHubs = [],
  initialParentId,
}: CreateHubDialogProps) {
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [icon, setIcon] = useState('folder')
  const [color, setColor] = useState(HUB_COLORS[0]?.value ?? DEFAULT_COLOR)
  const [parentId, setParentId] = useState<string | undefined>(initialParentId)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName('')
      setDescription('')
      setIcon('folder')
      setColor(HUB_COLORS[0]?.value ?? DEFAULT_COLOR)
      setParentId(initialParentId)
    }
  }, [open, initialParentId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    setLoading(true)
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        icon,
        color,
        parentId,
      })
      onOpenChange(false)
    } catch (error) {
      // Error handled by parent
    } finally {
      setLoading(false)
    }
  }

  // Flatten hub tree for parent selection
  const flattenHubs = (hubs: TemplateHubTree[], depth = 0): Array<{ hub: TemplateHubTree; depth: number }> => {
    return hubs.flatMap((hub) => [
      { hub, depth },
      ...flattenHubs(hub.children || [], depth + 1),
    ])
  }

  const flatHubs = flattenHubs(parentHubs)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create New Hub</DialogTitle>
            <DialogDescription>
              Create a new template hub to organize your templates.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-6">
            {/* Name */}
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium text-gray-900 dark:text-white">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter hub name"
                className={cn(
                  'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700',
                  'bg-white dark:bg-gray-800 text-gray-900 dark:text-white',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  'placeholder:text-gray-400'
                )}
                required
                autoFocus
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium text-gray-900 dark:text-white">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter hub description (optional)"
                rows={3}
                className={cn(
                  'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700',
                  'bg-white dark:bg-gray-800 text-gray-900 dark:text-white',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  'placeholder:text-gray-400 resize-none'
                )}
              />
            </div>

            {/* Icon Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-900 dark:text-white">
                Icon
              </label>
              <div className="flex gap-2">
                {HUB_ICON_OPTIONS.map((option) => {
                  const IconComponent = option.icon
                  return (
                    <button
                      key={option.name}
                      type="button"
                      onClick={() => setIcon(option.name)}
                      className={cn(
                        'p-3 rounded-lg border-2 transition-all',
                        icon === option.name
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      )}
                    >
                      <IconComponent className={cn(
                        'h-5 w-5',
                        icon === option.name
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-500'
                      )} />
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-900 dark:text-white">
                Color
              </label>
              <div className="flex flex-wrap gap-2">
                {HUB_COLORS.map((colorOption) => (
                  <button
                    key={colorOption.value}
                    type="button"
                    onClick={() => setColor(colorOption.value)}
                    className={cn(
                      'w-8 h-8 rounded-full transition-all',
                      `bg-gradient-to-br ${colorOption.value}`,
                      color === colorOption.value
                        ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-500 dark:ring-offset-gray-900'
                        : 'hover:scale-110'
                    )}
                    title={colorOption.name}
                  />
                ))}
              </div>
            </div>

            {/* Parent Hub Selection */}
            {parentHubs.length > 0 && (
              <div className="space-y-2">
                <label htmlFor="parentId" className="text-sm font-medium text-gray-900 dark:text-white">
                  Parent Hub
                </label>
                <select
                  id="parentId"
                  value={parentId || ''}
                  onChange={(e) => setParentId(e.target.value || undefined)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700',
                    'bg-white dark:bg-gray-800 text-gray-900 dark:text-white',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  )}
                >
                  <option value="">No parent (root level)</option>
                  {flatHubs.map(({ hub, depth }) => (
                    <option key={hub.id} value={hub.id}>
                      {'  '.repeat(depth)}{hub.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium',
                'text-gray-700 dark:text-gray-300',
                'hover:bg-gray-100 dark:hover:bg-gray-800',
                'transition-colors'
              )}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-semibold',
                'bg-gray-900 dark:bg-white text-white dark:text-gray-900',
                'hover:bg-gray-800 dark:hover:bg-gray-100',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors inline-flex items-center gap-2'
              )}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Hub
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface EditHubDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  hub: TemplateHub | null
  onSubmit: (id: string, data: UpdateHubRequest) => Promise<void>
  parentHubs?: TemplateHubTree[]
}

export function EditHubDialog({
  open,
  onOpenChange,
  hub,
  onSubmit,
  parentHubs = [],
}: EditHubDialogProps) {
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [icon, setIcon] = useState('folder')
  const [color, setColor] = useState(HUB_COLORS[0]?.value ?? DEFAULT_COLOR)
  const [parentId, setParentId] = useState<string | undefined>()
  const [isActive, setIsActive] = useState(true)

  // Load hub data when dialog opens
  useEffect(() => {
    if (open && hub) {
      setName(hub.name)
      setDescription(hub.description || '')
      setIcon(hub.icon || 'folder')
      setColor(hub.color || (HUB_COLORS[0]?.value ?? DEFAULT_COLOR))
      setParentId(hub.parentId)
      setIsActive(hub.isActive)
    }
  }, [open, hub])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!hub || !name.trim()) return

    setLoading(true)
    try {
      await onSubmit(hub.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        icon,
        color,
        parentId,
        isActive,
      })
      onOpenChange(false)
    } catch (error) {
      // Error handled by parent
    } finally {
      setLoading(false)
    }
  }

  // Flatten hub tree for parent selection, excluding the current hub and its children
  const flattenHubs = (
    hubs: TemplateHubTree[],
    depth = 0,
    excludeId?: string
  ): Array<{ hub: TemplateHubTree; depth: number }> => {
    return hubs.flatMap((h) => {
      if (h.id === excludeId) return []
      return [
        { hub: h, depth },
        ...flattenHubs(h.children || [], depth + 1, excludeId),
      ]
    })
  }

  const flatHubs = flattenHubs(parentHubs, 0, hub?.id)

  if (!hub) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Hub</DialogTitle>
            <DialogDescription>
              Update the hub details and settings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-6">
            {/* Name */}
            <div className="space-y-2">
              <label htmlFor="edit-name" className="text-sm font-medium text-gray-900 dark:text-white">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="edit-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter hub name"
                className={cn(
                  'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700',
                  'bg-white dark:bg-gray-800 text-gray-900 dark:text-white',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  'placeholder:text-gray-400'
                )}
                required
                autoFocus
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label htmlFor="edit-description" className="text-sm font-medium text-gray-900 dark:text-white">
                Description
              </label>
              <textarea
                id="edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter hub description (optional)"
                rows={3}
                className={cn(
                  'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700',
                  'bg-white dark:bg-gray-800 text-gray-900 dark:text-white',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  'placeholder:text-gray-400 resize-none'
                )}
              />
            </div>

            {/* Icon Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-900 dark:text-white">
                Icon
              </label>
              <div className="flex gap-2">
                {HUB_ICON_OPTIONS.map((option) => {
                  const IconComponent = option.icon
                  return (
                    <button
                      key={option.name}
                      type="button"
                      onClick={() => setIcon(option.name)}
                      className={cn(
                        'p-3 rounded-lg border-2 transition-all',
                        icon === option.name
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      )}
                    >
                      <IconComponent className={cn(
                        'h-5 w-5',
                        icon === option.name
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-500'
                      )} />
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-900 dark:text-white">
                Color
              </label>
              <div className="flex flex-wrap gap-2">
                {HUB_COLORS.map((colorOption) => (
                  <button
                    key={colorOption.value}
                    type="button"
                    onClick={() => setColor(colorOption.value)}
                    className={cn(
                      'w-8 h-8 rounded-full transition-all',
                      `bg-gradient-to-br ${colorOption.value}`,
                      color === colorOption.value
                        ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-500 dark:ring-offset-gray-900'
                        : 'hover:scale-110'
                    )}
                    title={colorOption.name}
                  />
                ))}
              </div>
            </div>

            {/* Parent Hub Selection */}
            {parentHubs.length > 0 && (
              <div className="space-y-2">
                <label htmlFor="edit-parentId" className="text-sm font-medium text-gray-900 dark:text-white">
                  Parent Hub
                </label>
                <select
                  id="edit-parentId"
                  value={parentId || ''}
                  onChange={(e) => setParentId(e.target.value || undefined)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700',
                    'bg-white dark:bg-gray-800 text-gray-900 dark:text-white',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  )}
                >
                  <option value="">No parent (root level)</option>
                  {flatHubs.map(({ hub: h, depth }) => (
                    <option key={h.id} value={h.id}>
                      {'  '.repeat(depth)}{h.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Active Status */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-900 dark:text-white">
                  Active Status
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Inactive hubs are hidden from users
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isActive}
                onClick={() => setIsActive(!isActive)}
                className={cn(
                  'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent',
                  'transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  isActive ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0',
                    'transition duration-200 ease-in-out',
                    isActive ? 'translate-x-5' : 'translate-x-0'
                  )}
                />
              </button>
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium',
                'text-gray-700 dark:text-gray-300',
                'hover:bg-gray-100 dark:hover:bg-gray-800',
                'transition-colors'
              )}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-semibold',
                'bg-gray-900 dark:bg-white text-white dark:text-gray-900',
                'hover:bg-gray-800 dark:hover:bg-gray-100',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors inline-flex items-center gap-2'
              )}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Changes
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
